"use client";

import * as React from "react";

/**
 * Portal target for Radix overlays: registered dialog branches, so menus never
 * land on the locked `document.body`.
 */
const overlayPortalStack: HTMLElement[] = [];
const listeners: Set<() => void> = new Set();

function notify(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return (): void => {
    listeners.delete(listener);
  };
}

export function pushDefaultOverlayPortal(el: HTMLElement): void {
  overlayPortalStack.push(el);
  notify();
}

export function popDefaultOverlayPortal(el: HTMLElement): void {
  const index: number = overlayPortalStack.lastIndexOf(el);
  if (index < 0) return;
  overlayPortalStack.splice(index, 1);
  notify();
}

/**
 * Picker branch → where surfaces nested in that host mount. A Radix layer
 * never dismisses on a click inside a `DismissableLayerBranch`; a dialog
 * mounted *in* the branch kept every popover, menu and select in it open.
 */
const nestedSurfaceHosts: WeakMap<HTMLElement, HTMLElement> = new WeakMap();

export function registerNestedSurfaceHost(
  branch: HTMLElement,
  host: HTMLElement,
): void {
  nestedSurfaceHosts.set(branch, host);
}

export function unregisterNestedSurfaceHost(branch: HTMLElement): void {
  nestedSurfaceHosts.delete(branch);
}

/** Where a surface opening over `el` mounts: its nested host, else `el`. */
export function nestedSurfaceHostFor(
  el: HTMLElement | null,
): HTMLElement | null {
  return el === null ? null : nestedSurfaceHosts.get(el) ?? el;
}

/** Stack top now. Pickers use `useOverlayPortal`: a render-time read goes stale. */
export function getDefaultOverlayPortal(): HTMLElement | null {
  return overlayPortalStack[overlayPortalStack.length - 1] ?? null;
}

/** Branch of the enclosing registered surface. Set by `DialogContent` (`popoverPortal`). */
export const OverlayPortalContext: React.Context<HTMLElement | null> =
  React.createContext<HTMLElement | null>(null);

/**
 * Explicit `container`, else the enclosing branch, else the stack top.
 * Stack is subscribed: a closed dialog's detached node never sticks.
 */
export function useOverlayPortal(
  container: HTMLElement | null | undefined,
): HTMLElement | undefined {
  const branch: HTMLElement | null = React.useContext(OverlayPortalContext);
  const top: HTMLElement | null = React.useSyncExternalStore(
    subscribe,
    getDefaultOverlayPortal,
    () => null,
  );
  return container ?? branch ?? top ?? undefined;
}
